import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import ConfirmDialog from '../../components/base/ConfirmDialog';

import { initialFormData, resetFormData } from './constants';
import OffresHeader from './OffresHeader';
import OffresTable from './OffresTable';
import OffreDetailDrawer from './OffreDetailDialog';
import OffreFormDialog from './OffreFormDialog';

const Offres = () => {
  const { user } = useAuth();
  const [offres, setOffres] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  const [intervenants, setIntervenants] = useState([]);
  const today = new Date().toISOString().split('T')[0];

  const [searchTerm, setSearchTerm] = useState('');

  // pour la liste des offres
  const [expandedRow, setExpandedRow] = useState(null);
  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const [formData, setFormData] = useState(initialFormData);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedOffre, setSelectedOffre] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleEditOffre = (offre) => {
    if (!offre || !offre.info_offre) {
      toast.error('Offre invalide ou incomplète');
      return;
    }

    const info = offre.info_offre;

    setSelectedOffre(offre);

    setFormData({
      titre_offre: info.titre_offre ?? '',
      description_offre: info.description_offre ?? '',
      
      // Récupération sécurisée de la cible du soin
      cible_offre: info.cible_offre ?? offre.cible_offre ?? info.cible ?? offre.cible ?? 'tous',

      duree_offre: info.duree_offre ?? 60,
      id_category: offre.id_category ?? '',
      status: Boolean(info.status),
      prix_offre: info.prix_offre ?? '',
      prix_special_soa: info.prix_special_soa ?? '',
      prix_client: info.prix_client ?? info.prix_offre ?? '',
      is_paid_offre: Boolean(info.is_paid_offre),
      is_accessible_participant: Boolean(info.is_accessible_participant),

      date_debut_dispo: offre.availability?.[0]?.date_debut?.slice(0, 10) ?? '',
      date_fin_dispo: offre.availability?.[0]?.date_fin?.slice(0, 10) ?? '',

      jours_off: (() => {
        const raw = info.jours_off || [];
        if (Array.isArray(raw)) return raw;
        try {
          return JSON.parse(raw);
        } catch {
          return [];
        }
      })(),

      duree_pause: info.duree_pause ?? 15,

      plage_horaire_matin_debut:
        info.plage_horaire_matin_debut?.slice(0, 5) ?? '09:00',
      plage_horaire_matin_fin:
        info.plage_horaire_matin_fin?.slice(0, 5) ?? '12:00',
      plage_horaire_aprem_debut:
        info.plage_horaire_aprem_debut?.slice(0, 5) ?? '14:00',
      plage_horaire_aprem_fin:
        info.plage_horaire_aprem_fin?.slice(0, 5) ?? '18:00',

      id_intervenant: offre.id_intervenant ?? '',
      format_offre: info.format_offre ?? 'presentiel',
      address_ws: info.address_ws ?? '',
      city_ws: info.city_ws ?? '',
      postal_code_ws: info.postal_code_ws ?? '',
      country_ws: info.country_ws ?? '',
      location_note_ws: info.location_note_ws ?? '',
      lat_ws: info.lat_ws ?? '',
      lng_ws: info.lng_ws ?? '',
      is_deplacement_possible: Boolean(info.is_deplacement_possible ?? false),
    });

    setIsEditMode(true);
    setOpen(true);
    setActiveStep(0);
  };

  // Récupération des catégories (préfixe selon le rôle)
// Récupération des catégories (préfixe selon le rôle)
  const fetchCategories = useCallback(async () => {
    try {
      // Mapping des rôles vers les préfixes d'URL
      const rolePaths = {
        3: 'intervenant',
        4: 'praticien', // Ajout du rôle praticien
      };

      // Par défaut 'admin' si le rôle ne correspond ni à 3 ni à 4
      const rolePath = rolePaths[user?.role] || 'admin';

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/${rolePath}/categories/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCategories(res.data.result || res.data || []);
    } catch (err) {
      console.error('Erreur catégories:', err);
    }
  }, [user?.role, token]);

 const fetchOffres = useCallback(async () => {
  try {
    // Si l'utilisateur est admin ou superadmin (rôle différent de 3 et 4)
    // On appelle la route globale admin. Sinon, on appelle la route spécifique.
    let rolePath = 'admin';
    if (user?.role === 3) {
      rolePath = 'intervenant';
    } else if (user?.role === 4) {
      rolePath = 'praticien';
    }

    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/${rolePath}/offer/g/lo/list_offer`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    setOffres(res.data.result || []);
  } catch (err) {
    console.error(err);
  }
}, [user?.role, token]);

  const fetchIntervenants = useCallback(async () => {
    if (user.role === 3) return;

    try {
      const rolePath = user.role === 3 ? 'intervenant' : 'admin';

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/${rolePath}/intervenant/l/list_intervenant`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIntervenants(res.data.result || []);
    } catch (err) {
      console.error('Erreur intervenants:', err);
    }
  }, [user.role, token]);

  const handleOpenDrawer = (offre) => {
    setSelectedOffre(offre);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setSelectedOffre(null);
    setOpenDrawer(false);
  };

  useEffect(() => {
    fetchOffres();
    fetchCategories();
    fetchIntervenants();
  }, [fetchOffres, fetchCategories, fetchIntervenants]);

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return (
          formData.titre_offre &&
          formData.description_offre &&
          formData.id_category &&
          ([3, 4, '3', '4'].includes(user?.role) || formData.id_intervenant)
        );
      case 1:
        return formData.prix_offre !== '' && formData.prix_special_soa !== '';
      case 2:
        return formData.date_debut_dispo && formData.date_fin_dispo;
      case 3:
        return (
          formData.plage_horaire_matin_debut && formData.plage_horaire_matin_fin
        );
      default:
        return false;
    }
  };

  // Payload propre sans type_offre
  const buildPayload = () => ({
    titre_offre: formData.titre_offre,
    description_offre: formData.description_offre,
    format_offre: formData.format_offre,
    cible_offre: formData.cible_offre || 'tous',
    duree_offre: Number(formData.duree_offre),
    id_category: formData.id_category,
    status: Boolean(formData.status),
    prix_offre: Number(formData.prix_offre),
    prix_special_soa: Number(formData.prix_special_soa),
    prix_client: Number(formData.prix_client || formData.prix_offre),
    is_paid_offre: Boolean(formData.is_paid_offre),
    is_accessible_participant: Boolean(formData.is_accessible_participant),

    date_debut_dispo: formData.date_debut_dispo,
    date_fin_dispo: formData.date_fin_dispo,

    jours_off: Array.isArray(formData.jours_off) ? formData.jours_off : [],
    duree_pause: Number(formData.duree_pause),

    plage_horaire_matin_debut: formData.plage_horaire_matin_debut,
    plage_horaire_matin_fin: formData.plage_horaire_matin_fin,
    plage_horaire_aprem_debut: formData.plage_horaire_aprem_debut,
    plage_horaire_aprem_fin: formData.plage_horaire_aprem_fin,

    id_intervenant: formData.id_intervenant,
    address_ws: formData.address_ws,
    city_ws: formData.city_ws,
    postal_code_ws: formData.postal_code_ws,
    country_ws: formData.country_ws,
    location_note_ws: formData.location_note_ws,

    lat_ws: formData.lat_ws,
    lng_ws: formData.lng_ws,
    is_deplacement_possible: Boolean(formData.is_deplacement_possible),
  });

 const handleSubmit = async () => {
    setLoading(true);

    try {
      // Détermination du préchemin selon le rôle (3 = intervenant, 4 = praticien, sinon admin)
      let rolePath = 'admin';
      if (user.role === 3) {
        rolePath = 'intervenant';
      } else if (user.role === 4) {
        rolePath = 'praticien';
      }

      // Pour l'intervenant (3) et le praticien (4), on cible leur propre id_user
      const targetId = [3, 4, '3', '4'].includes(user.role) 
        ? user.id_user 
        : formData.id_intervenant;

      if (isEditMode) {
        // UPDATE
        const res = await axios.put(
          `${process.env.REACT_APP_API_URL}/${rolePath}/offer/u/update/availabilty/${selectedOffre.id_offre}`,
          buildPayload(),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success(res.data?.message || 'Offre mise à jour');
      } else {
        // CREATE
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/${rolePath}/offer/a/add_offer/availabilty/${targetId}`,
          buildPayload(),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success(res.data?.message || 'Offre créée');
      }

      setOpen(false);
      setActiveStep(0);
      setIsEditMode(false);
      fetchOffres();
    } catch (err) {
      console.log('FULL ERROR =>', err);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Erreur inconnue';

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(resetFormData);
    setSelectedOffre(null);
    setIsEditMode(false);
    setActiveStep(0);
  };

  const toggleJourOff = (day) => {
    setFormData((prev) => ({
      ...prev,
      jours_off: prev.jours_off.includes(day)
        ? prev.jours_off.filter((d) => d !== day)
        : [...prev.jours_off, day],
    }));
  };

  const handleDeleteClick = (offre) => {
    setSelectedOffre(offre);
    setOpenConfirm(true);
  };

 const handleDeleteOffre = async () => {
    if (!selectedOffre) return;

    try {
      // Détermination du chemin selon le rôle (3 = intervenant, 4 = praticien, sinon admin)
      let rolePath = 'admin';
      if (user.role === 3) {
        rolePath = 'intervenant';
      } else if (user.role === 4) {
        rolePath = 'praticien';
      }

      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/${rolePath}/offer/d/delete/availabilty/${selectedOffre.id_offre}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(res.data?.message || 'Soin individuel supprimé avec succès');

      setOpenConfirm(false);
      setSelectedOffre(null);

      fetchOffres();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || 'Erreur suppression';

      toast.error(msg);
    }
  };

  const updateSlotStatus = async (slotId, newStatus) => {
    try {
      // Détermination du chemin selon le rôle (3 = intervenant, 4 = praticien, sinon admin)
      let rolePath = 'admin';
      if (user.role === 3) {
        rolePath = 'intervenant';
      } else if (user.role === 4) {
        rolePath = 'praticien';
      }

      const url = `${process.env.REACT_APP_API_URL}/${rolePath}/offer/u/slot/status/${slotId}`;

      await axios.put(
        url,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      setOffres((prev) =>
        prev.map((o) => ({
          ...o,
          availability: o.availability.map((a) => ({
            ...a,
            reserved_slots: a.reserved_slots.map((s) =>
              s.id_reserved_slot === slotId ? { ...s, status: newStatus } : s
            ),
          })),
        }))
      );

      toast.success('Statut du créneau mis à jour');
    } catch (err) {
      console.log('API ERROR =>', err?.response?.data || err.message);

      toast.error(err?.response?.data?.message || 'Erreur update slot');
    }
  };
  const syncOfferWithGoogle = async (offre) => {
    try {
      // Détermination du chemin selon le rôle (3 = intervenant, 4 = praticien, sinon admin)
      let rolePath = 'admin';
      if (user.role === 3) {
        rolePath = 'intervenant';
      } else if (user.role === 4) {
        rolePath = 'praticien';
      }

      toast.info('Synchronisation en cours...', { autoClose: 2000 });

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/${rolePath}/google-calendar/sync-by-offer/${offre.id_offre}`,
        {
          // Si l'utilisateur n'est ni intervenant (3) ni praticien (4), on envoie l'id_intervenant
          params: ![3, 4, '3', '4'].includes(user.role) ? { id_intervenant: offre.id_intervenant } : {},
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const conflicts = res.data?.result || [];

      if (conflicts.length > 0) {
        toast.warning(
          `⚠️ ${conflicts.length} créneau(x) bloqué(s) par Google Calendar`
        );
      } else {
        toast.success('✅ Aucun conflit détecté');
      }

      fetchOffres();
    } catch (err) {
      const msg =
        err?.response?.data?.message || 'Erreur synchronisation Google';
      toast.error(msg);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, bgcolor: '#F4F7FE', minHeight: '100vh' }}>
      <OffresHeader
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onCreateClick={() => {
          resetForm();
          setOpen(true);
        }}
      />

      <OffresTable
        offres={offres}
        searchTerm={searchTerm}
        expandedRow={expandedRow}
        onToggleRow={toggleRow}
        onOpenDrawer={handleOpenDrawer}
        onEditOffre={handleEditOffre}
        onSyncOffre={syncOfferWithGoogle}
        onDeleteClick={handleDeleteClick}
        onUpdateSlotStatus={updateSlotStatus}
      />

      <ConfirmDialog
        open={openConfirm}
        title="Suppression"
        description="Confirmer la suppression de cette offre ?"
        onCancel={() => {
          setOpenConfirm(false);
          setSelectedOffre(null);
        }}
        onConfirm={handleDeleteOffre}
      />

      <OffreDetailDrawer
        open={openDrawer}
        offre={selectedOffre}
        onClose={handleCloseDrawer}
        onUpdateSlotStatus={updateSlotStatus}
      />

      <OffreFormDialog
        open={open}
        onClose={() => setOpen(false)}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        loading={loading}
        isEditMode={isEditMode}
        formData={formData}
        setFormData={setFormData}
        categories={categories}
        intervenants={intervenants}
        user={user}
        today={today}
        isStepValid={isStepValid}
        onSubmit={handleSubmit}
        onToggleJourOff={toggleJourOff}
      />
    </Box>
  );
};

export default Offres;